import { useEffect, useState } from "react";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import { defaultUrl } from "../features/actions";
import { makeFetchRequest, makePutRequest } from "../utilities/common";
import { useDispatch, useSelector } from "react-redux";
import {
  selectRe_RenderedState,
  setSharedState,
} from "../features/sharedStateSlices/shareState";
import { chunkArray, searchThroughNames } from "../utilities/importantFns";
import { PagenateWithoutBackEnd } from "../components/pagination";
import StudentsArchive from "../layouts/archive/studentsArchive";
import StaffArchive from "../layouts/archive/StaffArchive";
import { FaSearch, FaChalkboardTeacher } from "react-icons/fa";
import "../assets/css/main.css";
import { User } from "lucide-react";

const Archive = () => {
  const dispatch = useDispatch();
  const reRender = useSelector((state) => selectRe_RenderedState(state));

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(1); // 1 = Pupils, 2 = Staff
  const [archivedChildren, setArchivedChildren] = useState([]);
  const [archivedTeachers, setArchivedTeachers] = useState([]);

  const [page, setPage] = useState(1); // Pages for Students
  const [pageT, setPageT] = useState(1); // Pages for Teachers
  const itemsPerPage = 10;

  const archivedChildrenPerPage = chunkArray(archivedChildren, itemsPerPage);
  const archivedTeachersPerPage = chunkArray(archivedTeachers, itemsPerPage);

  const pageIndex = page - 1;
  const pageIndexTeachers = pageT - 1;

  const fetchArchivedChildren = async () => {
    const classrooms = await makeFetchRequest(
      `${defaultUrl}/classroom`,
      false,
      true,
    );
    const childrenInArchive = await makeFetchRequest(
      `${defaultUrl}/students/archives`,
    );
    const filteredSearchStudents = await searchThroughNames(
      childrenInArchive,
      searchTerm,
    );

    const get_ClassName_Into_ChildrenData: any = [];
    filteredSearchStudents &&
      filteredSearchStudents.map((item: any) => {
        classrooms &&
          classrooms.map(({ id, name }: any) => {
            if (item.classroom_id === id) {
              item.classname = name;
              get_ClassName_Into_ChildrenData.push(item);
            }
          });
      });

    const sortByName = get_ClassName_Into_ChildrenData.sort((a: any, b: any) =>
      a.firstname.localeCompare(b.firstname),
    );
    setArchivedChildren(sortByName);
  };

  const fetchArchivedTeachers = async () => {
    const teachers = await makeFetchRequest(`${defaultUrl}/teachers/archives`);
    const filteredSearchStaff = await searchThroughNames(teachers, searchTerm);
    const sortByName = filteredSearchStaff.sort((a: any, b: any) =>
      a.firstname.localeCompare(b.firstname),
    );
    setArchivedTeachers(sortByName);
  };

  const handleArchive = (id: any) => {
    makePutRequest(`${defaultUrl}/unArchiveStudent/${id}`);
    dispatch(setSharedState());
  };

  const unArchiveTeacher = (id: any) => {
    makePutRequest(`${defaultUrl}/unArchiveTeacher/${id}`);
    dispatch(setSharedState());
  };

  const changePage = (index: number) => {
    setPage(index === 0 ? 1 : index + 1);
  };

  const changeStaffPage = (index: number) => {
    setPageT(index === 0 ? 1 : index + 1);
  };

  useEffect(() => {
    fetchArchivedChildren();
    fetchArchivedTeachers();
    // eslint-disable-next-line
  }, [activeTab, reRender, page, pageT, searchTerm]);

  return (
    <>
      {/* Top Application Layout Header Row */}
      <div className="w-full">
        <TopAdminNav name="Admin Manager" title="Data Archive Repository" />
      </div>

      {/* Main Workspace Frame */}
      <div className="flex overflow-y-auto register-scroll mt-2">
        <div className="block w-full h-[90vh] overflow-auto register-scroll">
          {/* Module Banner Deck */}
          <div className="flex">
            <div className="w-full flex items-center md:pr-6">
              <div className="w-full">
                <RegisterCard
                  title="System Backup & History Matrix"
                  details="Review, evaluate, and unarchive system records for historic student folders and teacher directories."
                />
              </div>
            </div>
          </div>

          {/* Dynamic Content Table Layout Container */}
          <div className="h-[78vh] rounded-2xl border-2 border-gray-100 px-6 py-5 attendance_bg mt-3 mr-4 overflow-y-auto register-scroll bg-white shadow-sm">
            {/* Filter Controls Row Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
              {/* Custom Segmented Selection Tab Toggle */}
              <div className="flex bg-[#F4F7FE] p-1.5 rounded-xl border border-gray-200/60 w-fit shrink-0 shadow-inner">
                <button
                  type="button"
                  onClick={() => setActiveTab(1)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold rounded-lg transition-all duration-150 ${
                    activeTab === 1
                      ? "bg-[#012063] text-white shadow-md scale-[1.02]"
                      : "text-[#012063CC] hover:bg-gray-200/50"
                  }`}
                >
                  <User
                    className={
                      activeTab === 1 ? "text-[#0096D1]" : "text-[#012063AA]"
                    }
                  />
                  Pupils Registry ({archivedChildren.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab(2)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold rounded-lg transition-all duration-150 ${
                    activeTab === 2
                      ? "bg-[#012063] text-white shadow-md scale-[1.02]"
                      : "text-[#012063CC] hover:bg-gray-200/50"
                  }`}
                >
                  <FaChalkboardTeacher
                    className={
                      activeTab === 2 ? "text-[#0096D1]" : "text-[#012063AA]"
                    }
                  />
                  Staff No ({archivedTeachers.length})
                </button>
              </div>

              {/* Seamless Integrated Native Search Filtering Bar */}
              <div className="w-full sm:w-72 relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-[#012063AA] text-xs">
                  <FaSearch />
                </div>
                <input
                  id="searchAttendanceInput"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search archived databases..."
                  className="w-full pl-10 pr-4 py-2.5 bg-[#F4F7FE] border-2 border-gray-200 focus:bg-white focus:border-[#0096D1] text-xs font-bold rounded-xl text-[#1f1f1f] placeholder-[#1f1f1f]/[0.4] outline-none transition-all"
                />
              </div>
            </div>

            {/* Dynamic Container Render Layer */}
            {activeTab === 1 ? (
              <div className="text-[#012063CC] w-full overflow-x-auto register-scroll animate-fade-in">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="h-2 w-2 rounded-full bg-[#0096D1]" />
                  <h3 className="text-left text-sm font-black tracking-tight text-slate-700 uppercase">
                    Pupil Storage Index
                  </h3>
                </div>

                <div className="w-full !min-w-[700px] mb-2">
                  <StudentsArchive
                    archived_ChildrenPerPage={archivedChildrenPerPage}
                    page_Index={pageIndex}
                    handle_Archive={handleArchive}
                  />
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <PagenateWithoutBackEnd
                    array={archivedChildrenPerPage}
                    page_change={changePage}
                    pageNum={page}
                    itemperPage={itemsPerPage}
                    totalItems={archivedChildren.length}
                  />
                </div>
              </div>
            ) : (
              <div className="text-[#012063CC] w-full overflow-x-auto register-scroll animate-fade-in">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="h-2 w-2 rounded-full bg-purple-600" />
                  <h3 className="text-left text-sm font-black tracking-tight text-slate-700 uppercase">
                    Faculty Storage Index
                  </h3>
                </div>

                <div className="w-full !min-w-[700px] mb-2">
                  <StaffArchive
                    unArchive_Teacher={unArchiveTeacher}
                    archived_TeachersPerPage={archivedTeachersPerPage}
                    pageIndex_Teachers={pageIndexTeachers}
                  />
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <PagenateWithoutBackEnd
                    array={archivedTeachersPerPage}
                    page_change={changeStaffPage}
                    pageNum={pageT}
                    itemperPage={itemsPerPage}
                    totalItems={archivedTeachers.length}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Archive;
